import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import MainLayout from "../../layouts/MainLayout"
import {
  getActivityLogById,
  getActivityLogFormData,
  getActivityLogs,
} from "../../services/activityLogService"

function ActivityLogsPage() {
  const theme = useTheme()
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"))
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"))

  const [logs, setLogs] = useState([])
  const [actionTypes, setActionTypes] = useState([])
  const [entityTypes, setEntityTypes] = useState([])

  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [selectedLog, setSelectedLog] = useState(null)

  const [search, setSearch] = useState("")
  const [actionTypeFilter, setActionTypeFilter] = useState("")
  const [entityTypeFilter, setEntityTypeFilter] = useState("")
  const [limit, setLimit] = useState(100)

  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  })

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, severity, message })
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const loadLogs = async () => {
    try {
      setLoading(true)

      const data = await getActivityLogs({
        search: search || undefined,
        action_type: actionTypeFilter || undefined,
        entity_type: entityTypeFilter || undefined,
        limit,
      })

      setLogs(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error(error)
      showSnackbar(
        error?.response?.data?.detail || "Failed to load activity logs",
        "error"
      )
    } finally {
      setLoading(false)
    }
  }

  const loadFormData = async () => {
    try {
      const data = await getActivityLogFormData()
      setActionTypes(Array.isArray(data?.action_types) ? data.action_types : [])
      setEntityTypes(Array.isArray(data?.entity_types) ? data.entity_types : [])
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    loadFormData()
    loadLogs()
  }, [])

  const handleSearch = () => {
    loadLogs()
  }

  const handleReset = () => {
    setSearch("")
    setActionTypeFilter("")
    setEntityTypeFilter("")
    setLimit(100)

    setTimeout(() => {
      loadLogs()
    }, 100)
  }

  const openDetails = async (id) => {
    try {
      const data = await getActivityLogById(id)
      setSelectedLog(data)
      setOpen(true)
    } catch (error) {
      console.error(error)
      showSnackbar("Failed to load log details", "error")
    }
  }

  const closeDetails = () => {
    setOpen(false)
    setSelectedLog(null)
  }

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 70 },
      {
        field: "action_type",
        headerName: "Action",
        minWidth: 150,
        flex: 1,
        renderCell: (params) => (
          <Chip label={params.value || "-"} size="small" color="primary" />
        ),
      },
      {
        field: "entity_type",
        headerName: "Entity",
        minWidth: 140,
        flex: 1,
      },
      {
        field: "reference_code",
        headerName: "Reference",
        minWidth: 150,
        flex: 1,
        valueGetter: (_, row) => row.reference_code || "-",
      },
      {
        field: "description",
        headerName: "Description",
        minWidth: 280,
        flex: 2,
        valueGetter: (_, row) => row.description || "-",
      },
      {
        field: "user_name",
        headerName: "User",
        minWidth: 160,
        flex: 1,
        valueGetter: (_, row) => row.user_name || `User ${row.user_id || "-"}`,
      },
      {
        field: "created_at",
        headerName: "Date",
        minWidth: 180,
        flex: 1,
        renderCell: (params) =>
          params.value ? new Date(params.value).toLocaleString() : "-",
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            variant="contained"
            size="small"
            onClick={() => openDetails(params.row.id)}
          >
            View
          </Button>
        ),
      },
    ],
    []
  )

  const columnVisibilityModel = useMemo(() => {
    if (isSmDown) {
      return {
        reference_code: false,
        user_name: false,
        created_at: false,
      }
    }

    if (isMdDown) {
      return {
        user_name: false,
      }
    }

    return {}
  }, [isMdDown, isSmDown])

  return (
    <MainLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "flex-start" },
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Activity Logs
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Track system actions, changes, validations and important events.
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleSearch}
          sx={{
            alignSelf: { xs: "stretch", sm: "auto" },
            minWidth: { xs: "100%", sm: 160 },
          }}
        >
          Refresh
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            fullWidth
            label="Search"
            placeholder="Search by action, entity, reference, user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <TextField
            select
            label="Action"
            value={actionTypeFilter}
            onChange={(e) => setActionTypeFilter(e.target.value)}
            sx={{ minWidth: { xs: "100%", md: 180 } }}
          >
            <MenuItem value="">All</MenuItem>
            {actionTypes.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Entity"
            value={entityTypeFilter}
            onChange={(e) => setEntityTypeFilter(e.target.value)}
            sx={{ minWidth: { xs: "100%", md: 180 } }}
          >
            <MenuItem value="">All</MenuItem>
            {entityTypes.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Limit"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            sx={{ minWidth: { xs: "100%", md: 120 } }}
          >
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={200}>200</MenuItem>
          </TextField>

          <Button
            variant="outlined"
            onClick={handleReset}
            sx={{
              alignSelf: { xs: "stretch", md: "center" },
              minWidth: { xs: "100%", md: 110 },
            }}
          >
            Reset
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Box sx={{ minWidth: { xs: 900, md: "100%" }, height: 560 }}>
            <DataGrid
              rows={logs}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 25]}
              columnVisibilityModel={columnVisibilityModel}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 },
                },
              }}
              sx={{
                border: 0,
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f8fafc",
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-cell": {
                  alignItems: "center",
                },
              }}
            />
          </Box>
        </Box>
      </Paper>

      <Dialog open={open} onClose={closeDetails} fullWidth maxWidth="md">
        <DialogTitle>Activity Log Details</DialogTitle>

        <DialogContent>
          {selectedLog && (
            <Stack spacing={2} mt={1}>
              <Typography>
                <strong>Action:</strong> {selectedLog.action_type || "-"}
              </Typography>

              <Typography>
                <strong>Entity:</strong> {selectedLog.entity_type || "-"}
              </Typography>

              <Typography>
                <strong>Reference:</strong> {selectedLog.reference_code || "-"}
              </Typography>

              <Typography>
                <strong>User:</strong>{" "}
                {selectedLog.user_name || `User ${selectedLog.user_id || "-"}`}
              </Typography>

              <Typography>
                <strong>Description:</strong> {selectedLog.description || "-"}
              </Typography>

              <Typography>
                <strong>Date:</strong>{" "}
                {selectedLog.created_at
                  ? new Date(selectedLog.created_at).toLocaleString()
                  : "-"}
              </Typography>

              <Typography fontWeight="bold">Old Values</Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f8fafc" }}>
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(selectedLog.old_values || {}, null, 2)}
                </pre>
              </Paper>

              <Typography fontWeight="bold">New Values</Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: "#f8fafc" }}>
                <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>
                  {JSON.stringify(selectedLog.new_values || {}, null, 2)}
                </pre>
              </Paper>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}

export default ActivityLogsPage